import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { LopSearchModal } from '../../components/LopSearchModal';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import { useIndices } from '../../hooks/useIndices';
import { useCrcies } from '../../hooks/useCrcies';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import { getSwapInterestTypes } from '../../api/swapInterestTypes';
import {
  createSwapUfLeg,
  getSwapUfAccounts,
  getSwapUfLegDefaults,
  swapUfErrorMessage,
} from '../../api/swapUf';
import type { SwapInterestType } from '../../types/SwapInterestType';
import type {
  SwapUfAccount,
  SwapUfAction,
  SwapUfCreateResult,
  SwapUfLegDefaults,
} from '../../types/SwapUf';
import { useTabDirty } from '../../tabs/useTabDirty';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import { SwapUfModal } from './SwapUfModal';
import './SwapUfCreateModal.css';
import { formatDateOnly } from '../../utils/dateTime';
import { LopDateInput } from '../../components/LopDateInput';

// Only these swap asset types may be created here (UF SWAP family).
const SWAP_ASSET_TYPE_IDS = new Set([45, 47, 48]);
const INTEREST_IBOR = 1;
const INTEREST_RFR = 2;

interface FormState {
  idxId: number | null;
  baseIdxRate: string;
  buyFee: string; // percent as typed
  sellFee: string;
  force: boolean;
  dealIdxRate: string;
  fxRatio: string;
  crcyId: number | null;
  assetTypeId: number | null;
  interestTypeId: number | null;
  fixedRate: string;
  interestAssetId: string;
  spread: string;
  settlementCrcyId: number | null;
  settlementDays: number;
  preMarket: boolean;
  resetFrequency: string;
  assetName: string;
  ticker: string;
}

const EMPTY_FORM: FormState = {
  idxId: null,
  baseIdxRate: '',
  buyFee: '',
  sellFee: '',
  force: false,
  dealIdxRate: '',
  fxRatio: '',
  crcyId: null,
  assetTypeId: null,
  interestTypeId: null,
  fixedRate: '',
  interestAssetId: '',
  spread: '',
  settlementCrcyId: null,
  settlementDays: 2,
  preMarket: false,
  resetFrequency: '',
  assetName: '',
  ticker: '',
};

const fracToPct = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(Number((v * 100).toFixed(6)));
const numStr = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);
const pctToFrac = (s: string): number | null => {
  const x = s.trim();
  if (x === '' || !Number.isFinite(Number(x))) return null;
  return Number(x) / 100;
};
const toNum = (s: string): number | null => {
  const x = s.trim();
  if (x === '' || !Number.isFinite(Number(x))) return null;
  return Number(x);
};
const todayIso = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

interface Props {
  initialAction?: SwapUfAction;
  initialAcc?: string | null;
  onClose: () => void;
  onCreated: (r: SwapUfCreateResult) => void;
}

export function SwapUfCreateModal({ initialAction = 'new', initialAcc = null, onClose, onCreated }: Props) {
  const [action, setAction] = useState<SwapUfAction>(initialAction);
  const [acc, setAcc] = useState<string | null>(initialAcc);
  const [dealDate, setDealDate] = useState<string>(todayIso());
  const [qty, setQty] = useState('');
  const [isShort, setIsShort] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [defaults, setDefaults] = useState<SwapUfLegDefaults | null>(null);
  const [noHistory, setNoHistory] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [accounts, setAccounts] = useState<SwapUfAccount[]>([]);
  const [interestTypes, setInterestTypes] = useState<SwapInterestType[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);

  useTabDirty(dirty);

  const { data: indices } = useIndices();
  const { data: crcies } = useCrcies();
  const { data: assetTypes } = useAssetTypes();

  useEffect(() => {
    let cancelled = false;
    getSwapUfAccounts()
      .then((rows) => !cancelled && setAccounts(rows))
      .catch((e) => !cancelled && setError(swapUfErrorMessage(e)));
    getSwapInterestTypes()
      .then((rows) => !cancelled && setInterestTypes(rows))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // User edit: marks the form dirty. Prefill from the server uses setForm directly.
  const patch = useCallback((p: Partial<FormState>) => {
    setDirty(true);
    setForm((f) => ({ ...f, ...p }));
  }, []);

  // Prefill whenever the account or the deal date changes.
  useEffect(() => {
    if (acc === null || !dealDate) {
      setDefaults(null);
      setNoHistory(false);
      return;
    }
    let cancelled = false;
    setLoadingDefaults(true);
    getSwapUfLegDefaults(acc, dealDate)
      .then((d) => {
        if (cancelled) return;
        setDefaults(d);
        setNoHistory(d === null);
        if (!d) return;
        setForm((f) => ({
          ...f,
          idxId: d.idxId,
          baseIdxRate: numStr(d.baseIdxRate),
          buyFee: fracToPct(d.buyFeePercent),
          sellFee: fracToPct(d.sellFeePercent),
          fxRatio: numStr(d.crcyRatioAtDealDate),
          crcyId: d.crcyId,
          assetTypeId: d.assetTypeId,
          interestTypeId: d.interestTypeId,
          fixedRate: fracToPct(d.fixedInterestRate),
          interestAssetId: numStr(d.interestAssetId),
          spread: fracToPct(d.spreadPercent),
          settlementCrcyId: d.settlementCrcyId,
          settlementDays: d.settlementDays ?? 2,
          preMarket: d.isPreMarketExchange ?? false,
          resetFrequency: numStr(d.resetFrequencyMonths),
        }));
      })
      .catch((e) => {
        if (!cancelled) setError(swapUfErrorMessage(e));
      })
      .finally(() => !cancelled && setLoadingDefaults(false));
    return () => {
      cancelled = true;
    };
  }, [acc, dealDate]);

  const eligible = useMemo(
    () => accounts.filter((a) => a.hasActiveChain === (action !== 'new')),
    [accounts, action],
  );
  const accOptions = useMemo(
    () =>
      eligible.map((a) => ({
        id: a.acc,
        name: `${a.fundName ?? ''}${a.activeChainIdxName ? ` [${a.activeChainIdxName}]` : ''}`,
      })),
    [eligible],
  );
  const idxOptions = useMemo(
    () => (indices ?? []).map((i) => ({ id: i.idxId, name: i.idxName })),
    [indices],
  );
  const crcyOptions = useMemo(
    () => (crcies ?? []).map((c) => ({ id: c.crcyId, name: c.crcyName })),
    [crcies],
  );
  const assetTypeOptions = useMemo(
    () => (assetTypes ?? []).filter((a) => SWAP_ASSET_TYPE_IDS.has(a.assetTypeId)),
    [assetTypes],
  );
  const pickerColumns = useMemo(
    () => [
      { field: 'assetId', headerName: t('swapUf.create.assetColId') },
      { field: 'assetName', headerName: t('swapUf.create.assetColName') },
      { field: 'symbol', headerName: t('swapUf.create.assetColSymbol') },
    ],
    [],
  );

  const changeAction = (a: SwapUfAction) => {
    if (a === action) return;
    setAction(a);
    setDirty(true);
    // The account list differs per action; keep the account only if still eligible.
    setAcc((cur) => {
      const ok = accounts.some((x) => x.acc === cur && x.hasActiveChain === (a !== 'new'));
      return ok ? cur : null;
    });
    setDefaults(null);
  };

  const adjust = action !== 'new';
  const baseNum = toNum(form.baseIdxRate);
  const buyFrac = pctToFrac(form.buyFee) ?? 0;
  const sellFrac = pctToFrac(form.sellFee) ?? 0;
  const computedDeal =
    baseNum === null ? null : action === 'decrease' ? baseNum * (1 - sellFrac) : baseNum * (1 + buyFrac);
  const computedDealText = computedDeal === null ? '' : String(Number(computedDeal.toFixed(6)));

  const save = async () => {
    const qtyNum = toNum(qty);
    if (acc === null) return setError(t('swapUf.create.accInvalid'));
    if (!dealDate) return setError(t('swapUf.create.dateInvalid'));
    if (qtyNum === null || qtyNum <= 0) return setError(t('swapUf.create.qtyInvalid'));
    setError(null);
    setSaving(true);
    try {
      const r = await createSwapUfLeg({
        acc,
        action,
        dealDate,
        qty: qtyNum,
        isShort: action === 'new' ? isShort : null,
        idxId: form.idxId,
        baseIdxRate: baseNum,
        dealIdxRate: form.force ? toNum(form.dealIdxRate) : null,
        fxRatioAtDeal: toNum(form.fxRatio),
        assetTypeId: form.assetTypeId,
        crcyId: form.crcyId,
        spreadPercent: pctToFrac(form.spread),
        interestTypeId: form.interestTypeId,
        fixedInterestRate: form.interestTypeId === INTEREST_IBOR ? pctToFrac(form.fixedRate) : null,
        interestAssetId: form.interestTypeId === INTEREST_RFR ? toNum(form.interestAssetId) : null,
        buyFeePercent: pctToFrac(form.buyFee),
        sellFeePercent: pctToFrac(form.sellFee),
        settlementCrcyId: form.settlementCrcyId,
        settlementDays: form.settlementDays,
        isPreMarketExchange: form.preMarket,
        referenceIdxId: null,
        conversionRatio: null,
        countryCode: null,
        continentId: null,
        isGovernment: null,
        linkageTypeId: null,
        basisTypeId: null,
        benchmarkRateId: null,
        benchmarkRatePeriodId: null,
        resetFrequencyMonths: action === 'new' ? toNum(form.resetFrequency) : null,
        assetName: form.assetName.trim() || null,
        ticker: form.ticker.trim() || null,
      });
      setDirty(false);
      onCreated(r);
    } catch (e) {
      setError(swapUfErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const actions: { id: SwapUfAction; label: string }[] = [
    { id: 'new', label: t('swapUf.create.actionNew') },
    { id: 'increase', label: t('swapUf.create.actionIncrease') },
    { id: 'decrease', label: t('swapUf.create.actionDecrease') },
  ];

  return (
    <SwapUfModal
      title={t('swapUf.create.title')}
      onClose={onClose}
      footer={
        <>
          <LopButton id="swap-uf-create-save" variant="primary" loading={saving} onClick={save}>
            {t('swapUf.create.save')}
          </LopButton>
          <LopButton variant="secondary" disabled={saving} onClick={onClose}>
            {t('swapUf.modal.cancel')}
          </LopButton>
        </>
      }
    >
      <div className="swap-uf-create">
        <div className="swap-uf-create__actions" role="radiogroup" aria-label={t('swapUf.create.action')}>
          {actions.map((a) => (
            <button
              key={a.id}
              type="button"
              role="radio"
              aria-checked={action === a.id}
              data-action={a.id}
              className={`swap-uf-create__action${action === a.id ? ' is-active' : ''}`}
              onClick={() => changeAction(a.id)}
            >
              {a.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="swap-uf-error" role="alert" id="swap-uf-create-error">
            {error}
          </div>
        )}

        <div className="swap-uf-create__grid">
          <div className="swap-uf-create__field swap-uf-create__field--wide">
            <label htmlFor="swap-uf-create-acc">
              {t('swapUf.create.account')}{' '}
              <span className="swap-uf-info">
                ({adjust ? t('swapUf.create.accountAdjustHint') : t('swapUf.create.accountNewHint')})
              </span>
            </label>
            <LopSearchableSelect
              id="swap-uf-create-acc"
              value={acc}
              options={accOptions}
              onChange={(v) => {
                setAcc(v);
                setDirty(true);
              }}
              placeholder={t('swapUf.create.pickAccount')}
            />
          </div>

          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-date">{t('swapUf.create.dealDate')}</label>
            <LopDateInput
              id="swap-uf-create-date"
              value={dealDate}
              onChange={(v) => {
                setDealDate(v);
                setDirty(true);
              }}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-qty">{t('swapUf.create.qty')}</label>
            <input
              id="swap-uf-create-qty"
              type="number"
              min="0"
              step="any"
              value={qty}
              onChange={(e) => {
                setQty(e.target.value);
                setDirty(true);
              }}
            />
          </div>
          {action === 'new' && (
            <div className="swap-uf-create__field">
              <span className="swap-uf-create__label">{t('swapUf.create.long')} / {t('swapUf.create.short')}</span>
              <div className="swap-uf-create__toggle" role="radiogroup">
                <button
                  type="button"
                  role="radio"
                  aria-checked={!isShort}
                  className={!isShort ? 'is-active' : ''}
                  onClick={() => {
                    setIsShort(false);
                    setDirty(true);
                  }}
                >
                  {t('swapUf.create.long')}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isShort}
                  className={isShort ? 'is-active' : ''}
                  onClick={() => {
                    setIsShort(true);
                    setDirty(true);
                  }}
                >
                  {t('swapUf.create.short')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="swap-uf-info swap-uf-create__line">
          {loadingDefaults && t('swapUf.create.loadingDefaults')}
          {!loadingDefaults && noHistory && t('swapUf.create.noHistory')}
          {!loadingDefaults && adjust && defaults && defaults.hasActiveChain &&
            t('swapUf.create.chainInfo', {
              qty: defaults.chainOpenQty === null ? '-' : defaults.chainOpenQty.toLocaleString('he-IL'),
              resetDate: formatDateOnly(defaults.resetDate) || '-',
            })}
        </div>

        <div className="swap-uf-create__grid">
          <div className="swap-uf-create__field swap-uf-create__field--wide">
            <label htmlFor="swap-uf-create-idx">{t('swapUf.create.index')}</label>
            <LopSearchableSelect
              id="swap-uf-create-idx"
              value={form.idxId}
              options={idxOptions}
              onChange={(v) => patch({ idxId: v })}
              disabled={adjust}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-base">{t('swapUf.create.baseIdxRate')}</label>
            <input
              id="swap-uf-create-base"
              type="number"
              step="any"
              value={form.baseIdxRate}
              onChange={(e) => patch({ baseIdxRate: e.target.value })}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-buy">{t('swapUf.create.buyFee')}</label>
            <input
              id="swap-uf-create-buy"
              type="number"
              step="any"
              value={form.buyFee}
              onChange={(e) => patch({ buyFee: e.target.value })}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-sell">{t('swapUf.create.sellFee')}</label>
            <input
              id="swap-uf-create-sell"
              type="number"
              step="any"
              value={form.sellFee}
              onChange={(e) => patch({ sellFee: e.target.value })}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-deal">{t('swapUf.create.dealIdxRate')}</label>
            <input
              id="swap-uf-create-deal"
              type="number"
              step="any"
              readOnly={!form.force}
              value={form.force ? form.dealIdxRate : computedDealText}
              onChange={(e) => patch({ dealIdxRate: e.target.value })}
            />
            <label className="swap-uf-create__check">
              <input
                id="swap-uf-create-force"
                type="checkbox"
                checked={form.force}
                onChange={(e) =>
                  patch({
                    force: e.target.checked,
                    dealIdxRate: e.target.checked ? computedDealText : '',
                  })
                }
              />
              {t('swapUf.create.forceDealIdx')}
            </label>
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-fx">{t('swapUf.create.fxRatio')}</label>
            <input
              id="swap-uf-create-fx"
              type="number"
              step="any"
              value={form.fxRatio}
              onChange={(e) => patch({ fxRatio: e.target.value })}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-crcy">{t('swapUf.create.crcy')}</label>
            <LopSearchableSelect
              id="swap-uf-create-crcy"
              value={form.crcyId}
              options={crcyOptions}
              onChange={(v) => patch({ crcyId: v })}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-atype">{t('swapUf.create.assetType')}</label>
            <select
              id="swap-uf-create-atype"
              value={form.assetTypeId ?? ''}
              onChange={(e) => patch({ assetTypeId: e.target.value === '' ? null : Number(e.target.value) })}
            >
              <option value="" />
              {assetTypeOptions.map((a) => (
                <option key={a.assetTypeId} value={a.assetTypeId}>
                  {a.assetTypeId} - {a.assetTypeName}
                </option>
              ))}
            </select>
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-itype">{t('swapUf.create.interestType')}</label>
            <select
              id="swap-uf-create-itype"
              value={form.interestTypeId ?? ''}
              onChange={(e) => patch({ interestTypeId: e.target.value === '' ? null : Number(e.target.value) })}
            >
              <option value="" />
              {interestTypes.map((i) => (
                <option key={i.swapInterestTypeId} value={i.swapInterestTypeId}>
                  {i.swapInterestTypeName}
                </option>
              ))}
            </select>
          </div>
          {form.interestTypeId === INTEREST_IBOR && (
            <div className="swap-uf-create__field">
              <label htmlFor="swap-uf-create-fixed">{t('swapUf.create.fixedRate')}</label>
              <input
                id="swap-uf-create-fixed"
                type="number"
                step="any"
                value={form.fixedRate}
                onChange={(e) => patch({ fixedRate: e.target.value })}
              />
            </div>
          )}
          {form.interestTypeId === INTEREST_RFR && (
            <div className="swap-uf-create__field">
              <label htmlFor="swap-uf-create-iasset">{t('swapUf.create.interestAsset')}</label>
              <div className="swap-uf-create__inline">
                <input
                  id="swap-uf-create-iasset"
                  type="number"
                  value={form.interestAssetId}
                  onChange={(e) => patch({ interestAssetId: e.target.value })}
                />
                <button
                  type="button"
                  className="swap-uf-btn swap-uf-btn--small"
                  aria-label={t('swapUf.create.interestAssetPick')}
                  onClick={() => setAssetPickerOpen(true)}
                >
                  <Search size={14} />
                </button>
              </div>
            </div>
          )}
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-spread">{t('swapUf.create.spread')}</label>
            <input
              id="swap-uf-create-spread"
              type="number"
              step="any"
              value={form.spread}
              onChange={(e) => patch({ spread: e.target.value })}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-scrcy">{t('swapUf.create.settlementCrcy')}</label>
            <LopSearchableSelect
              id="swap-uf-create-scrcy"
              value={form.settlementCrcyId}
              options={crcyOptions}
              onChange={(v) => patch({ settlementCrcyId: v })}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-sdays">{t('swapUf.create.settlementDays')}</label>
            <select
              id="swap-uf-create-sdays"
              value={form.settlementDays}
              onChange={(e) => patch({ settlementDays: Number(e.target.value) })}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
            </select>
          </div>
          <div className="swap-uf-create__field">
            <label className="swap-uf-create__check swap-uf-create__check--solo">
              <input
                id="swap-uf-create-premarket"
                type="checkbox"
                checked={form.preMarket}
                onChange={(e) => patch({ preMarket: e.target.checked })}
              />
              {t('swapUf.create.preMarket')}
            </label>
          </div>
          {action === 'new' && (
            <div className="swap-uf-create__field">
              <label htmlFor="swap-uf-create-reset">{t('swapUf.create.resetFrequency')}</label>
              <input
                id="swap-uf-create-reset"
                type="number"
                min="1"
                step="1"
                value={form.resetFrequency}
                onChange={(e) => patch({ resetFrequency: e.target.value })}
              />
            </div>
          )}
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-name">{t('swapUf.create.assetName')}</label>
            <input
              id="swap-uf-create-name"
              type="text"
              placeholder={t('swapUf.create.autoCreated')}
              value={form.assetName}
              onChange={(e) => patch({ assetName: e.target.value })}
            />
          </div>
          <div className="swap-uf-create__field">
            <label htmlFor="swap-uf-create-ticker">{t('swapUf.create.ticker')}</label>
            <input
              id="swap-uf-create-ticker"
              type="text"
              placeholder={t('swapUf.create.autoCreated')}
              value={form.ticker}
              onChange={(e) => patch({ ticker: e.target.value })}
            />
          </div>
        </div>
      </div>
      {assetPickerOpen && (
        <LopSearchModal
          title={t('swapUf.create.interestAssetPick')}
          table="ASSETS"
          columns={pickerColumns}
          onSelect={(row) => {
            if (row.assetId != null) patch({ interestAssetId: String(row.assetId) });
          }}
          onClose={() => setAssetPickerOpen(false)}
        />
      )}
    </SwapUfModal>
  );
}
